import React, { useEffect, useMemo, useState } from "react";
import routeAdminUsers from "./routes";
import { useSelector } from "react-redux";
import { routeAuth } from "../AuthPage";
import { Redirect } from "react-router-dom";

import "./styles.scss";

import {
    Badge,
    Button,
    Card,
    Col,
    Divider,
    Dropdown,
    Input,
    Modal,
    Progress,
    Row,
    Segmented,
    Space,
    Statistic,
    Switch,
    Table,
    Tag,
    Typography,
    notification,
} from "antd";

import {
    CrownOutlined,
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined,
    SettingOutlined,
    UserOutlined,
} from "@ant-design/icons";

import { UserHeader } from "../../components/UserHeader/UserHeader";
import EditModal from "../../components/Modals/EditModal/EditModal";
import { deleteRequest, getRequest, postRequest, putRequest } from "../../services/requestController";

const { Title, Text } = Typography;

const ROLE = {
    SUPERADMIN: 1,
    MANAGER: 2,
};

const roleMeta = (role) => {
    const r = Number(role);
    if (r === ROLE.SUPERADMIN) return { label: "Суперадмин", color: "gold", icon: <CrownOutlined /> };
    return { label: "Менеджер", color: "blue", icon: <UserOutlined /> };
};

const pickUsersPayload = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.users)) return payload.users;
    if (payload.data && Array.isArray(payload.data.users)) return payload.data.users;
    return [];
};

const AdminUsersPage = () => {
    const token = useSelector((state) => state.token);

    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);

    const [query, setQuery] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

    const [createOpen, setCreateOpen] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);

    const [editOpen, setEditOpen] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    const fetchUsers = async () => {
        if (!token) return;
        setLoading(true);
        try {
            const resp = await getRequest("/api/admin/users", token);
            if (!resp?.success) {
                notification.error({
                    message: "Ошибка",
                    description: resp?.error?.message || "Не удалось получить пользователей",
                });
                return;
            }
            const list = pickUsersPayload(resp.data);
            const normalized = list
                .map((u) => ({
                    userId: Number(u?.userId),
                    login: String(u?.login ?? ""),
                    role: Number(u?.role ?? ROLE.MANAGER),
                    permissions: u?.permissions || {},
                }))
                .filter((u) => Number.isFinite(u.userId) && u.userId > 0 && u.login);

            normalized.sort((a, b) => a.userId - b.userId);
            setUsers(normalized);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [token]);

    const totals = useMemo(() => {
        const total = users.length;
        const managers = users.filter((u) => Number(u.role) === ROLE.MANAGER).length;
        const superadmins = users.filter((u) => Number(u.role) === ROLE.SUPERADMIN).length;
        const coverage = total ? Math.round((managers / total) * 100) : 0;
        return { total, managers, superadmins, coverage };
    }, [users]);

    const filteredUsers = useMemo(() => {
        const q = String(query || "").trim().toLowerCase();
        const rf = String(roleFilter || "all");

        return users.filter((u) => {
            const matchesQuery =
                !q ||
                String(u.login || "").toLowerCase().includes(q) ||
                String(u.userId || "").includes(q);

            const matchesRole = rf === "all" ? true : Number(u.role) === Number(rf);

            return matchesQuery && matchesRole;
        });
    }, [users, query, roleFilter]);

    const openCreate = () => setCreateOpen(true);
    const closeCreate = () => {
        setCreateOpen(false);
        setCreateLoading(false);
    };

    const openEdit = (user) => {
        setEditingUser(user);
        setEditOpen(true);
    };

    const closeEdit = () => {
        setEditOpen(false);
        setEditLoading(false);
        setEditingUser(null);
    };

    const submitCreate = async (values) => {
        const login = typeof values?.login === "string" ? values.login.trim() : "";
        const password = typeof values?.password === "string" ? values.password : "";
        const role = Number(values?.role);

        if (!login || login.length < 5) {
            notification.error({ message: "Ошибка", description: "Логин должен быть не короче 5 символов" });
            return;
        }
        if (!password || password.length < 5) {
            notification.error({ message: "Ошибка", description: "Пароль должен быть не короче 5 символов" });
            return;
        }
        if (![ROLE.MANAGER, ROLE.SUPERADMIN].includes(role)) {
            notification.error({ message: "Ошибка", description: "Некорректная роль" });
            return;
        }

        setCreateLoading(true);
        try {
            const resp = await postRequest("/api/admin/users", { login, password, role }, token);
            if (!resp?.success) {
                notification.error({
                    message: "Ошибка",
                    description: resp?.error?.message || "Не удалось создать пользователя",
                });
                return;
            }

            const created = resp?.data?.data || resp?.data?.user || resp?.data;
            const nextUser = {
                userId: Number(created?.userId),
                login: String(created?.login ?? login),
                role: Number(created?.role ?? role),
            };

            if (!Number.isFinite(nextUser.userId) || nextUser.userId <= 0) {
                await fetchUsers();
            } else {
                setUsers((prev) => {
                    const exists = prev.some((u) => Number(u.userId) === Number(nextUser.userId));
                    const merged = exists ? prev.map((u) => (u.userId === nextUser.userId ? nextUser : u)) : [nextUser, ...prev];
                    merged.sort((a, b) => a.userId - b.userId);
                    return merged;
                });
            }

            notification.success({
                message: "Успешно",
                description: `Пользователь создан: ${nextUser.login}`,
            });
            closeCreate();
        } finally {
            setCreateLoading(false);
        }
    };

    const submitEdit = async (values) => {
        const userId = Number(editingUser?.userId);
        if (!Number.isFinite(userId) || userId <= 0) return;

        const patch = {};
        const login = typeof values?.login === "string" ? values.login.trim() : "";
        const password = typeof values?.password === "string" ? values.password : "";
        const role = values?.role;

        if (typeof values?.login !== "undefined") {
            if (!login || login.length < 5) {
                notification.error({ message: "Ошибка", description: "Логин должен быть не короче 5 символов" });
                return;
            }
            patch.login = login;
        }

        if (typeof values?.password !== "undefined") {
            if (password && password.length < 5) {
                notification.error({ message: "Ошибка", description: "Пароль должен быть не короче 5 символов" });
                return;
            }
            if (password) patch.password = password;
        }

        if (typeof role !== "undefined") {
            const nr = Number(role);
            if (![ROLE.MANAGER, ROLE.SUPERADMIN].includes(nr)) {
                notification.error({ message: "Ошибка", description: "Некорректная роль" });
                return;
            }
            patch.role = nr;
        }

        if (Object.keys(patch).length === 0) {
            notification.info({ message: "Нет изменений", description: "Измените поля и сохраните" });
            return;
        }

        setEditLoading(true);
        try {
            const resp = await putRequest(`/api/admin/users/${userId}`, patch, token);
            if (!resp?.success) {
                notification.error({
                    message: "Ошибка",
                    description: resp?.error?.message || "Не удалось обновить пользователя",
                });
                return;
            }

            const updated = resp?.data?.data || resp?.data?.user || resp?.data;
            const nextUser = {
                userId,
                login: String(updated?.login ?? patch.login ?? editingUser?.login ?? ""),
                role: Number(updated?.role ?? patch.role ?? editingUser?.role ?? ROLE.MANAGER),
            };

            setUsers((prev) => prev.map((u) => (u.userId === userId ? { ...u, ...nextUser } : u)));
            notification.success({ message: "Успешно", description: `Пользователь обновлён: ${nextUser.login}` });
            closeEdit();
        } finally {
            setEditLoading(false);
        }
    };

    const confirmDelete = (record) => {
        const userId = Number(record?.userId);
        const login = String(record?.login || "");
        const role = Number(record?.role);

        Modal.confirm({
            title: "Удалить пользователя?",
            content: (
                <Space direction="vertical" size={4}>
                    <Text>
                        Будет удалён <Text strong>userId={userId}</Text> (<Text code>{login}</Text>)
                    </Text>
                    {role === ROLE.SUPERADMIN ? (
                        <Text type="warning">Пользователей с ролью “Суперадмин” удалять нельзя.</Text>
                    ) : (
                        <Text type="secondary">Действие необратимо.</Text>
                    )}
                </Space>
            ),
            okText: "Удалить",
            okButtonProps: { danger: true, disabled: role === ROLE.SUPERADMIN },
            cancelText: "Отмена",
            onOk: async () => {
                const resp = await deleteRequest(`/api/admin/users/${userId}`, token);
                if (!resp?.success) {
                    notification.error({
                        message: "Ошибка",
                        description: resp?.error?.message || "Не удалось удалить пользователя",
                    });
                    return;
                }
                setUsers((prev) => prev.filter((u) => u.userId !== userId));
                notification.success({ message: "Удалено", description: `Пользователь удалён: ${login}` });
            },
        });
    };

    const columns = useMemo(
        () => [
            {
                title: "Пользователь",
                key: "user",
                render: (_, record) => {
                    const meta = roleMeta(record.role);
                    return (
                        <Space direction="vertical" size={2}>
                            <Space size={10} align="center">
                                <Badge status={Number(record.role) === ROLE.SUPERADMIN ? "warning" : "processing"} />
                                <Text strong>{record.login}</Text>
                                <Tag color={meta.color} icon={meta.icon}>
                                    {meta.label}
                                </Tag>
                                <Tag bordered={false}>ID: {record.userId}</Tag>
                            </Space>
                            <Text type="secondary">Доступ: панель администратора · управление контентом · операционные задачи</Text>
                        </Space>
                    );
                },
            },
            {
                title: "Роль",
                dataIndex: "role",
                key: "role",
                width: 160,
                sorter: (a, b) => Number(a.role) - Number(b.role),
                render: (role) => {
                    const meta = roleMeta(role);
                    return (
                        <Tag color={meta.color} icon={meta.icon}>
                            {meta.label}
                        </Tag>
                    );
                },
            },
            {
                title: "Права",
                key: "permissions",
                width: 180,
                render: (_, record) => {
                    if (Number(record.role) === ROLE.SUPERADMIN) return <Tag color="gold">Полный доступ</Tag>;
                    return (
                        <Space direction="vertical" size={2}>
                            <Space size={6}>
                                <Switch
                                    size="small"
                                    checked={!!record.permissions?.canManageBru}
                                    onChange={async (checked) => {
                                        const resp = await putRequest(`/api/admin/users/${record.userId}`, { permissions: { canManageBru: checked } }, token);
                                        if (resp?.success) {
                                            setUsers((prev) => prev.map((u) => u.userId === record.userId ? { ...u, permissions: { ...u.permissions, canManageBru: checked } } : u));
                                            notification.success({ message: checked ? "BRU разрешён" : "BRU запрещён" });
                                        }
                                    }}
                                />
                                <Text style={{ fontSize: 12 }}>Управление BRU</Text>
                            </Space>
                        </Space>
                    );
                },
            },
            {
                title: "Действия",
                key: "actions",
                width: 220,
                render: (_, record) => (
                    <Space>
                        <Button icon={<EditOutlined />} onClick={() => openEdit(record)}>
                            Редактировать
                        </Button>
                        <Dropdown
                            trigger={["click"]}
                            menu={{
                                items: [
                                    {
                                        key: "copy",
                                        label: "Скопировать логин",
                                        onClick: async () => {
                                            try {
                                                await navigator.clipboard.writeText(String(record.login || ""));
                                                notification.success({ message: "Скопировано", description: record.login });
                                            } catch {
                                                notification.error({ message: "Ошибка", description: "Не удалось скопировать" });
                                            }
                                        },
                                    },
                                    {
                                        key: "delete",
                                        danger: true,
                                        label: "Удалить",
                                        disabled: Number(record.role) === ROLE.SUPERADMIN,
                                        onClick: () => confirmDelete(record),
                                        icon: <DeleteOutlined />,
                                    },
                                ],
                            }}
                        >
                            <Button icon={<SettingOutlined />} />
                        </Dropdown>
                    </Space>
                ),
            },
        ],
        [users]
    );

    if (!token) return <Redirect to={routeAuth()} />;

    const editInitialValues = editingUser
        ? {
            login: editingUser.login,
            password: "",
            role: editingUser.role,
        }
        : {};

    return (
        <>
            <div className="container admin-users-page">
                <UserHeader />

                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} md={14}>
                        <Typography>
                            <Title level={3} style={{ marginBottom: 0 }}>
                                Менеджеры и админ-пользователи
                            </Title>
                            <Text type="secondary">Создание, редактирование, удаление и быстрый поиск по пользователям</Text>
                        </Typography>
                    </Col>

                    <Col xs={24} md={10} style={{ textAlign: "right" }}>
                        <Space wrap>
                            <Button icon={<ReloadOutlined />} onClick={fetchUsers} loading={loading}>
                                Обновить
                            </Button>
                            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                                Добавить менеджера
                            </Button>
                        </Space>
                    </Col>
                </Row>

                <Divider />

                <Row gutter={[16, 16]}>
                    <Col xs={24} md={6}>
                        <Card bordered={false} className="admin-users-card admin-users-card--accent">
                            <Statistic title="Всего пользователей" value={totals.total} prefix={<EyeOutlined />} />
                            <Text type="secondary">Учётные записи доступа</Text>
                        </Card>
                    </Col>
                    <Col xs={24} md={6}>
                        <Card bordered={false} className="admin-users-card">
                            <Statistic title="Менеджеры" value={totals.managers} prefix={<UserOutlined />} />
                            <Progress percent={totals.coverage} size="small" strokeColor="#1677ff" />
                        </Card>
                    </Col>
                    <Col xs={24} md={6}>
                        <Card bordered={false} className="admin-users-card">
                            <Statistic
                                title="Суперадмины"
                                value={totals.superadmins}
                                prefix={<CrownOutlined />}
                                valueStyle={{ color: "#ad6800" }}
                            />
                            <Text type="secondary">Удаление запрещено</Text>
                        </Card>
                    </Col>
                    <Col xs={24} md={6}>
                        <Card bordered={false} className="admin-users-card">
                            <Statistic title="Статус безопасности" value="OK" />
                            <Text type="secondary">Пароли меняются через редактирование</Text>
                        </Card>
                    </Col>
                </Row>

                <Divider />

                <Card
                    bordered={false}
                    className="admin-users-card"
                    title="Каталог пользователей"
                    extra={
                        <Space wrap>
                            <Input
                                allowClear
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Поиск по логину или ID"
                                prefix={<SearchOutlined />}
                                style={{ width: 280 }}
                            />
                            <Segmented
                                value={roleFilter}
                                onChange={setRoleFilter}
                                options={[
                                    { label: "Все", value: "all" },
                                    { label: "Менеджеры", value: ROLE.MANAGER },
                                    { label: "Суперадмины", value: ROLE.SUPERADMIN },
                                ]}
                            />
                        </Space>
                    }
                >
                    <Table
                        columns={columns}
                        dataSource={filteredUsers}
                        loading={loading}
                        rowKey={(r) => String(r.userId)}
                        onChange={(nextPagination) =>
                            setPagination({ current: nextPagination.current, pageSize: nextPagination.pageSize || 10 })
                        }
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: filteredUsers.length,
                            showSizeChanger: true,
                            pageSizeOptions: [10, 20, 50],
                            showTotal: (total) => `Всего: ${total}`,
                        }}
                    />
                </Card>
            </div>

            <EditModal
                open={createOpen}
                title="Добавить пользователя"
                onClose={closeCreate}
                onSubmit={submitCreate}
                confirmLoading={createLoading}
                initialValues={{ login: "", password: "", role: ROLE.MANAGER }}
                okText="Создать"
                cancelText="Отмена"
                width={560}
                fields={[
                    {
                        type: "input",
                        name: "login",
                        label: "Логин",
                        rules: [{ required: true, message: "Введите логин (минимум 5 символов)" }],
                        props: { autoComplete: "off" },
                    },
                    {
                        type: "input",
                        name: "password",
                        label: "Пароль",
                        rules: [{ required: true, message: "Введите пароль (минимум 5 символов)" }],
                        props: { type: "password", autoComplete: "new-password" },
                    },
                    {
                        type: "select",
                        name: "role",
                        label: "Роль",
                        options: [
                            { label: "Менеджер (2)", value: ROLE.MANAGER },
                            { label: "Суперадмин (1)", value: ROLE.SUPERADMIN },
                        ],
                        rules: [{ required: true, message: "Выберите роль" }],
                    },
                ]}
            />

            <EditModal
                open={editOpen}
                title={editingUser ? `Редактирование: ${editingUser.login} (ID ${editingUser.userId})` : "Редактирование пользователя"}
                onClose={closeEdit}
                onSubmit={submitEdit}
                confirmLoading={editLoading}
                initialValues={editInitialValues}
                okText="Сохранить"
                cancelText="Отмена"
                width={560}
                fields={[
                    {
                        type: "input",
                        name: "login",
                        label: "Логин",
                        rules: [{ required: true, message: "Введите логин (минимум 5 символов)" }],
                        props: { autoComplete: "off" },
                    },
                    {
                        type: "input",
                        name: "password",
                        label: "Новый пароль (необязательно)",
                        rules: [],
                        props: { type: "password", autoComplete: "new-password" },
                        placeholder: "Оставьте пустым, чтобы не менять",
                    },
                    {
                        type: "select",
                        name: "role",
                        label: "Роль",
                        options: [
                            { label: "Менеджер (2)", value: ROLE.MANAGER },
                            { label: "Суперадмин (1)", value: ROLE.SUPERADMIN },
                        ],
                        rules: [{ required: true, message: "Выберите роль" }],
                    },
                ]}
            />
        </>
    );
};

export { routeAdminUsers };

export default AdminUsersPage;